import { c as _c } from "react/compiler-runtime";
import { AnyTranslationsFn } from "@/types/locale";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAsterisk, IconRestore } from "@tabler/icons-react";
import { ZodObject } from "zod/v4";
import React, { useCallback, useEffect } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useTranslations } from "next-intl";
import { EditButton } from "@components/common/buttons/edit-button";
import { AlertDialogWrapper } from "@components/common/containers/alert-dialog-wrapper";
import { FormFieldAbstract } from "@components/common/forms/form-field-abstract";
import { FormTextField } from "@components/common/forms/form-text-field";
import { ButtonWithTooltip } from "@components/ui/custom/button-with-tooltip";
import { Form } from "@components/ui/form";
import { Switch } from "@components/ui/switch";
import { zt } from "@lib/forms/zt";
import { useAnyTranslations } from "@lib/i18n/utils";
import { APP_NAME_DOMAIN } from "@features/application/app-consts";
import { ActivityMemberEntity, ActivityMemberInputView, ContributorFormInputView, ContributorFormOutputView, MemberNotifeeType } from "@features/proposals/types/member";
interface MemberNotifeeEditDialogProps {
  type: MemberNotifeeType;
  member: ActivityMemberEntity;
  notification?: ContributorFormOutputView;
  isMe: boolean;
  patchMember: (member: ActivityMemberEntity, body: Partial<ActivityMemberInputView>) => Promise<undefined | ActivityMemberEntity>;
}

/**
 * Validation schema for a notification consent form.
 * For regular users (isMe=true): only allows editing consent acceptance.
 * For admins/coordinators (isMe=false, canPatch=true): allows setting third-party consent.
 */
const getValidationSchema = (t: AnyTranslationsFn, isMe: boolean, notifeeType: string): ZodObject => {
  if (isMe) {
    // User editing their own consent - only accepted field
    return zt.object({
      eventProfile: zt.object({
        notifee: zt.object({
          [notifeeType]: zt.object({
            accepted: zt.boolean(t, true)
          })
        })
      })
    });
  }

  // Admin/coordinator editing - includes third-party fields
  return zt.object({
    eventProfile: zt.object({
      notifee: zt.object({
        [notifeeType]: zt.object({
          accepted: zt.boolean(t, true),
          acceptedByThirdPartyReason: zt.string(t, true, 10, 500)
        })
      })
    })
  });
};

/**
 * Edit the dialog component for member notification preferences.
 * Allows editing notification consent with role-based access control.
 */
export const MemberNotifeeEditDialog = t0 => {
  const $ = _c(101);
  const {
    type,
    member,
    notification,
    isMe,
    patchMember
  } = t0;
  const tFormsErrors = useAnyTranslations("common.forms.errors");
  const t = useTranslations("features.proposals.ui.memberNotifeeStatus");
  const tCommon = useTranslations("common");
  const currentAcceptedState = notification?.accepted ?? false;
  let getInitialFormData;
  let t1;
  let t2;
  if ($[0] !== isMe || $[1] !== notification || $[2] !== tFormsErrors || $[3] !== type) {
    const schema = getValidationSchema(tFormsErrors, isMe, type);
    let t3;
    if ($[7] !== notification?.accepted || $[8] !== notification?.acceptedByThirdPartyReason || $[9] !== type) {
      t3 = () => ({
        eventProfile: {
          notifee: {
            [type]: {
              accepted: notification?.accepted ?? false,
              acceptedByThirdPartyReason: notification?.acceptedByThirdPartyReason ?? ""
            }
          }
        }
      });
      $[7] = notification?.accepted;
      $[8] = notification?.acceptedByThirdPartyReason;
      $[9] = type;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    notification?.accepted;
    notification?.acceptedByThirdPartyReason;
    getInitialFormData = t3;
    t2 = useForm;
    t1 = zodResolver(schema);
    $[0] = isMe;
    $[1] = notification;
    $[2] = tFormsErrors;
    $[3] = type;
    $[4] = getInitialFormData;
    $[5] = t1;
    $[6] = t2;
  } else {
    getInitialFormData = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  let t3;
  if ($[11] !== getInitialFormData) {
    t3 = getInitialFormData();
    $[11] = getInitialFormData;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t1 || $[14] !== t3) {
    t4 = {
      resolver: t1,
      mode: "all",
      defaultValues: t3
    };
    $[13] = t1;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const form = t2(t4);
  const {
    isDirty,
    isValid
  } = form.formState;
  const t5 = `eventProfile.notifee.${type}.accepted` as string;
  let t6;
  if ($[16] !== currentAcceptedState || $[17] !== form.control || $[18] !== t5) {
    t6 = {
      control: form.control,
      name: t5,
      defaultValue: currentAcceptedState
    };
    $[16] = currentAcceptedState;
    $[17] = form.control;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const watchedAccepted = useWatch(t6);
  let t7;
  if ($[20] !== form || $[21] !== getInitialFormData || $[22] !== isDirty || $[23] !== isMe || $[24] !== member || $[25] !== patchMember || $[26] !== type) {
    t7 = () => form.handleSubmit(data => {
      if (!isDirty) {
        return Promise.reject();
      }
      const toSend = {
        eventProfile: {
          notifee: {
            [type]: {
              accepted: data.eventProfile?.notifee?.[type]?.accepted,
              ...(!isMe && data.eventProfile?.notifee?.[type]?.acceptedByThirdPartyReason && {
                acceptedByThirdPartyReason: data.eventProfile.notifee[type].acceptedByThirdPartyReason
              })
            }
          } as Record<MemberNotifeeType, ContributorFormInputView>
        }
      };
      return patchMember(member, toSend).then(updatedEntity => {
        if (updatedEntity) {
          form.reset(getInitialFormData());
        }
      }).catch(_temp);
    })();
    $[20] = form;
    $[21] = getInitialFormData;
    $[22] = isDirty;
    $[23] = isMe;
    $[24] = member;
    $[25] = patchMember;
    $[26] = type;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const onSubmitHandler = t7;
  let t8;
  if ($[28] !== form || $[29] !== getInitialFormData) {
    t8 = () => form.reset(getInitialFormData());
    $[28] = form;
    $[29] = getInitialFormData;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const onCloseHandler = t8;
  let t9;
  if ($[31] !== form || $[32] !== getInitialFormData || $[33] !== isDirty) {
    t9 = () => {
      if (form && !isDirty) {
        form.reset(getInitialFormData());
      }
    };
    $[31] = form;
    $[32] = getInitialFormData;
    $[33] = isDirty;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== form || $[36] !== getInitialFormData || $[37] !== isDirty || $[38] !== notification) {
    t10 = [form, getInitialFormData, isDirty, notification];
    $[35] = form;
    $[36] = getInitialFormData;
    $[37] = isDirty;
    $[38] = notification;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  useEffect(t9, t10);
  let t11;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <EditButton />;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== isMe || $[42] !== t) {
    t12 = isMe ? t("editTitle.self") : t("editTitle.other");
    $[41] = isMe;
    $[42] = t;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== tCommon) {
    t13 = tCommon("words.verbs.save");
    $[44] = tCommon;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  const t14 = !isDirty || !isValid;
  let t15;
  if ($[46] !== onCloseHandler || $[47] !== onSubmitHandler) {
    t15 = {
      onAction: onSubmitHandler,
      onClose: onCloseHandler
    };
    $[46] = onCloseHandler;
    $[47] = onSubmitHandler;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== isMe || $[50] !== t) {
    t16 = isMe ? t("editDescription.self") : t("editDescription.other");
    $[49] = isMe;
    $[50] = t;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== tCommon) {
    t17 = tCommon("words.verbs.reset");
    $[52] = tCommon;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== form || $[55] !== getInitialFormData) {
    t18 = () => form.reset(getInitialFormData());
    $[54] = form;
    $[55] = getInitialFormData;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  const t19 = !isDirty;
  let t20;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <IconRestore />;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== t17 || $[59] !== t18 || $[60] !== t19) {
    t21 = <div className="flex items-center justify-end gap-2 border-b py-1"><ButtonWithTooltip tooltipContent={t17} size="icon" variant="ghost" onClick={t18} disabled={t19}>{t20}</ButtonWithTooltip></div>;
    $[58] = t17;
    $[59] = t18;
    $[60] = t19;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  const t22 = `eventProfile.notifee.${type}.accepted`;
  let t23;
  if ($[62] !== isMe || $[63] !== t || $[64] !== watchedAccepted) {
    t23 = watchedAccepted ? isMe ? t("form.accepted.label.self.give") : t("form.accepted.label.other.give") : isMe ? t("form.accepted.label.self.revoke") : t("form.accepted.label.other.revoke");
    $[62] = isMe;
    $[63] = t;
    $[64] = watchedAccepted;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== isMe || $[67] !== t || $[68] !== watchedAccepted) {
    t24 = watchedAccepted ? isMe ? t("form.accepted.description.self.give") : t("form.accepted.description.other.give") : isMe ? t("form.accepted.description.self.revoke") : t("form.accepted.description.other.revoke");
    $[66] = isMe;
    $[67] = t;
    $[68] = watchedAccepted;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== member.rights.canPatch || $[71] !== t22 || $[72] !== t23 || $[73] !== t24) {
    t25 = <FormFieldAbstract name={t22} rights={member.rights.canPatch} className="max-w-sm" label={t23} description={t24} renderControl={_temp3} />;
    $[70] = member.rights.canPatch;
    $[71] = t22;
    $[72] = t23;
    $[73] = t24;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== isMe || $[76] !== member.rights.canPatch || $[77] !== t || $[78] !== type || $[79] !== watchedAccepted) {
    t26 = !isMe && <FormTextField name={`eventProfile.notifee.${type}.acceptedByThirdPartyReason`} rights={member.rights.canPatch} label={watchedAccepted ? t("form.thirdPartyReason.label.give") : t("form.thirdPartyReason.label.revoke")} description={watchedAccepted ? t("form.thirdPartyReason.description.give") : t("form.thirdPartyReason.description.revoke")} required={true} textarea={true} />;
    $[75] = isMe;
    $[76] = member.rights.canPatch;
    $[77] = t;
    $[78] = type;
    $[79] = watchedAccepted;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] !== t25 || $[82] !== t26) {
    t27 = <form className="space-y-4" onSubmit={_temp2}>{t25}{t26}</form>;
    $[81] = t25;
    $[82] = t26;
    $[83] = t27;
  } else {
    t27 = $[83];
  }
  let t28;
  if ($[84] !== form || $[85] !== t27) {
    t28 = <Form {...form}>{t27}</Form>;
    $[84] = form;
    $[85] = t27;
    $[86] = t28;
  } else {
    t28 = $[86];
  }
  let t29;
  if ($[87] !== isMe || $[88] !== tCommon) {
    t29 = !isMe && <div className="flex items-center gap-2 text-sm text-muted-foreground"><div className="w-5 justify-items-center"><IconAsterisk className="h-3 w-3 text-destructive" /></div><span>{tCommon("forms.errors.required")}</span></div>;
    $[87] = isMe;
    $[88] = tCommon;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  let t30;
  if ($[90] !== t21 || $[91] !== t28 || $[92] !== t29) {
    t30 = <div className="flex flex-col gap-4">{t21}{t28}{t29}</div>;
    $[90] = t21;
    $[91] = t28;
    $[92] = t29;
    $[93] = t30;
  } else {
    t30 = $[93];
  }
  let t31;
  if ($[94] !== t12 || $[95] !== t13 || $[96] !== t14 || $[97] !== t15 || $[98] !== t16 || $[99] !== t30) {
    t31 = <AlertDialogWrapper trigger={t11} title={t12} actionTitle={t13} actionDisabled={t14} handlers={t15} description={t16}>{t30}</AlertDialogWrapper>;
    $[94] = t12;
    $[95] = t13;
    $[96] = t14;
    $[97] = t15;
    $[98] = t16;
    $[99] = t30;
    $[100] = t31;
  } else {
    t31 = $[100];
  }
  return t31;
};
function _temp(e) {
  console.error(`[${APP_NAME_DOMAIN}] ${e}`);
}
function _temp2(e_0) {
  return e_0.preventDefault();
}
function _temp3(field, isDirty_0) {
  return <Switch {...field} checked={field.value ?? false} onCheckedChange={field.onChange} className={isDirty_0 ? "bg-gray-200 dark:bg-gray-700" : ""} />;
}